import { useState, useEffect } from 'react';
import InfoIconSVG from '../../utilities/icons/InfoIconSVG';
import { DoubleDownArrowsIcon } from '../../utilities/icons/CommonIcons';
import { motion, AnimatePresence } from 'framer-motion';

export default function RealEstatesHeroSection({ onScrollToContent }) {
  const [showInfo, setShowInfo] = useState(false);

  // Fallback para vh en navegadores sin soporte de 100dvh
  useEffect(() => {
    const setVh = () => {
      document.documentElement.style.setProperty(
        '--vh',
        `${window.innerHeight * 0.01}px`
      );
    };
    setVh();
    window.addEventListener('resize', setVh);
    return () => window.removeEventListener('resize', setVh);
  }, []);

  // Animaciones
  const infoVariants = {
    hidden: { opacity: 0, x: 80 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  };
  const buttonVariants = {
    hidden: { opacity: 0, y: 60 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut', delay: 0.25 },
    },
  };

  // Cuando la imagen carga, mostrar info con animación
  const handleImageLoad = () => {
    setTimeout(() => setShowInfo(true), 100); // Pequeño delay para entrada
  };

  return (
    <div
      id="real-estates-hero-section"
      className="relative w-full overflow-hidden"
      style={{
        height: 'calc(100dvh - 8rem)',
        minHeight: 'calc(var(--vh, 1vh) * 100 - 8rem)',
      }}
    >
      <div className="absolute inset-0 z-0">
        <img
          src="/SalesImage.png"
          alt="Background"
          className="object-cover w-full h-full"
          onLoad={handleImageLoad}
        />
        <AnimatePresence>
          {showInfo && (
            <motion.div
              initial="hidden"
              animate="visible"
              exit="hidden"
              variants={infoVariants}
              className="absolute top-0 right-0 h-full w-full md:w-1/2 lg:w-1/3 bg-black bg-opacity-60 flex flex-col items-center justify-center text-center p-6"
            >
              <>
                <motion.div
                  className="mb-4"
                  initial={{ opacity: 0, x: 80 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, ease: 'easeOut', delay: 0.2 }}
                >
                  <InfoIconSVG className="w-16 h-16 lg:h-20 lg:w-20" />
                </motion.div>
                <motion.h2
                  className="text-white text-xl sm:text-2xl lg:text-3xl font-heading tracking-tight font-bold mb-4 text-center"
                  initial={{ opacity: 0, x: 80 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, ease: 'easeOut', delay: 0.3 }}
                >
                  Información general
                </motion.h2>
                <motion.p
                  className="text-sm text-gray-300 sm:text-md lg:text-lg tracking-tight font-semibold text-center"
                  initial={{ opacity: 0, x: 80 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, ease: 'easeOut', delay: 0.4 }}
                >
                  Toda la información está sujeta a cambios de precio, áreas u
                  otras condiciones, la venta, o la retirada sin previo aviso.
                  Las áreas y detalles son aproximados y la información deberá
                  ser confirmada por el cliente. Todos los derechos sobre los
                  contenidos, fotografías, vídeos y gráficos son reservados y no
                  podrán ser usados en otro sitio web.
                </motion.p>
              </>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <AnimatePresence>
        {showInfo && (
          <motion.div
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={buttonVariants}
            className="absolute bottom-4 w-full flex justify-center"
          >
            <button onClick={onScrollToContent} className="focus:outline-none">
              <motion.span
                initial={{ y: 0 }}
                animate={{ y: [0, 20, 0] }}
                transition={{
                  repeat: Infinity,
                  duration: 1,
                  ease: 'easeInOut',
                }}
                style={{ display: 'inline-block' }}
              >
                <DoubleDownArrowsIcon className="w-12 h-12 fill-white" />
              </motion.span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
